import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, ChevronUp, Trash2, Filter } from 'lucide-react';

const DebugConsole = () => {
  const [logs, setLogs] = useState([]);
  const [isOpen, setIsOpen] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'log', 'error', 'warn', 'info', 'debug'
  const [searchTerm, setSearchTerm] = useState('');
  const logsEndRef = useRef(null);
  const originalConsole = useRef({});

  useEffect(() => {
    // Store original console methods
    originalConsole.current = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info,
      debug: console.debug
    };

    // Override console methods
    const addLog = (level, args) => {
      const timestamp = new Date().toLocaleTimeString();
      const message = args
        .map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg, null, 2);
            } catch (e) {
              return String(arg);
            }
          }
          return String(arg);
        })
        .join(' ');

      setLogs(prev => {
        // Generate a truly unique ID using timestamp + random + index
        const uniqueId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${prev.length}`;
        const newLogs = [...prev, { timestamp, level, message, id: uniqueId }];
        // Keep only last 500 logs to prevent memory issues
        return newLogs.slice(-500);
      });
    };

    console.log = (...args) => {
      originalConsole.current.log(...args);
      addLog('log', args);
    };

    console.error = (...args) => {
      originalConsole.current.error(...args);
      addLog('error', args);
    };

    console.warn = (...args) => {
      originalConsole.current.warn(...args);
      addLog('warn', args);
    };

    console.info = (...args) => {
      originalConsole.current.info(...args);
      addLog('info', args);
    };

    console.debug = (...args) => {
      originalConsole.current.debug(...args);
      addLog('debug', args);
    };

    // Cleanup on unmount
    return () => {
      console.log = originalConsole.current.log;
      console.error = originalConsole.current.error;
      console.warn = originalConsole.current.warn;
      console.info = originalConsole.current.info;
      console.debug = originalConsole.current.debug;
    };
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new logs arrive
    if (logsEndRef.current && !isMinimized) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isMinimized]);

  const clearLogs = () => {
    setLogs([]);
  };

  const getLogColor = (level) => {
    switch (level) {
      case 'error':
        return 'text-red-400 bg-red-400/10';
      case 'warn':
        return 'text-yellow-400 bg-yellow-400/10';
      case 'info':
        return 'text-blue-400 bg-blue-400/10';
      case 'debug':
        return 'text-gray-400 bg-gray-400/10';
      default:
        return 'text-gray-300 bg-gray-800/50';
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2"
      >
        <span>Show Debug Console</span>
      </button>
    );
  }

  const filteredLogs = logs.filter(log => {
    const matchesFilter = filter === 'all' || log.level === filter;
    const matchesSearch = searchTerm === '' || 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.timestamp.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[600px] bg-gray-900 border border-gray-700 rounded-lg shadow-2xl flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <span className="text-white font-semibold text-sm">Debug Console</span>
          <span className="text-gray-400 text-xs">
            ({filteredLogs.length} / {logs.length} logs)
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {/* Filter Dropdown */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-gray-700 text-white text-xs px-2 py-1 rounded border border-gray-600"
            title="Filter logs"
          >
            <option value="all">All</option>
            <option value="log">Log</option>
            <option value="error">Error</option>
            <option value="warn">Warn</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
          <button
            onClick={clearLogs}
            className="text-gray-400 hover:text-red-400 transition-colors"
            title="Clear logs"
          >
            <Trash2 size={16} />
          </button>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-gray-400 hover:text-white transition-colors"
            title={isMinimized ? 'Expand' : 'Minimize'}
          >
            {isMinimized ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-white transition-colors"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Search Bar */}
      {!isMinimized && (
        <div className="px-4 py-2 border-b border-gray-700">
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 text-white text-xs px-3 py-1 rounded border border-gray-600 focus:outline-none focus:border-blue-500"
          />
        </div>
      )}

      {/* Logs Container */}
      {!isMinimized && (
        <div className="flex-1 overflow-y-auto max-h-[500px] p-2 font-mono text-xs">
          {logs.length === 0 ? (
            <div className="text-gray-500 text-center py-4">No logs yet...</div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-gray-500 text-center py-4">No logs match your filter/search...</div>
          ) : (
            filteredLogs.map((log) => (
                <div
                  key={log.id}
                  className={`mb-1 p-2 rounded ${getLogColor(log.level)} break-words`}
                >
                  <div className="flex items-start space-x-2">
                    <span className="text-gray-500 text-xs flex-shrink-0">
                      {log.timestamp}
                    </span>
                    <span className="text-gray-400 text-xs flex-shrink-0">
                      [{log.level.toUpperCase()}]
                    </span>
                    <span className="flex-1 whitespace-pre-wrap">{log.message}</span>
                  </div>
                </div>
              ))
          )}
          <div ref={logsEndRef} />
        </div>
      )}
    </div>
  );
};

export default DebugConsole;

